import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2, LogIn, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import homepageBackground from "@/assets/abigail-background-hp.png";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, ApiError } from "@/lib/api";
import { fetchMe, meQueryKey } from "@/lib/auth";
import { useQueryClient } from "@tanstack/react-query";

const SSO_MESSAGES: Record<string, string> = {
  erro: "Não conseguimos concluir o acesso pelo Hotmart. Tente novamente.",
  cancelado: "Você cancelou a autorização no Hotmart.",
  state: "Sua sessão de acesso expirou. Tente entrar pelo Hotmart novamente.",
  "sem-acesso": "Não encontramos uma compra ativa para o e-mail da sua conta Hotmart.",
  "nao-configurado": "O acesso pelo Hotmart está temporariamente indisponível.",
  expirado: "Seu tempo de uso da plataforma (6 meses após a data da compra) expirou.",
};

export const Route = createFileRoute("/")({
  validateSearch: (search: Record<string, unknown>) => ({
    sso: typeof search["sso"] === "string" ? (search["sso"] as string) : undefined,
  }),
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { sso } = Route.useSearch();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState<string | null>(
    sso ? (SSO_MESSAGES[sso] ?? SSO_MESSAGES["erro"]!) : null,
  );
  const [sent, setSent] = useState<string | null>(null);
  const [ssoEnabled, setSsoEnabled] = useState(false);

  useEffect(() => {
    let active = true;
    void fetchMe().then((me) => {
      if (!active) return;
      queryClient.setQueryData(meQueryKey, me);
      if (me) {
        void navigate({ to: "/bem-vindo", replace: true });
        return;
      }
      setChecking(false);
    });
    return () => {
      active = false;
    };
  }, [navigate, queryClient]);

  useEffect(() => {
    let active = true;
    void fetch("/api/public/hotmart/sso-status", { credentials: "include" })
      .then((r) => (r.ok ? (r.json() as Promise<{ enabled: boolean }>) : null))
      .then((data) => {
        if (active && data) setSsoEnabled(data.enabled);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  function handleHotmartClick(event: React.MouseEvent<HTMLAnchorElement>) {
    if (!email.trim()) {
      event.preventDefault();
      setSent(null);
      setError("Preencha seu e-mail antes de continuar com o Hotmart.");
    }
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setSent(null);

    if (!email.trim()) {
      setError("Preencha seu e-mail para continuar.");
      return;
    }

    setLoading(true);

    const normalized = email.trim().toLowerCase();

    try {
      await api.post("/api/auth/request-access", { email: normalized });
      setSent(normalized);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : "Não conseguimos enviar seu link de acesso agora. Tente novamente em alguns instantes.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="relative flex min-h-screen items-center justify-center px-4 pt-[48vh]">
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden bg-background">
        <img
          src={homepageBackground}
          alt=""
          className="h-full w-full object-cover"
        />
      </div>

      <section className="w-full max-w-sm">
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="sr-only">
              E-mail
            </Label>
            <div className="relative">
              <Mail className="pointer-events-none absolute top-1/2 left-4 h-4 w-4 -translate-y-1/2 text-primary" />
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                maxLength={255}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="Digite seu email"
                className="h-12 border-border bg-background/60 pl-11 text-sm text-foreground placeholder:text-muted-foreground"
              />
            </div>
          </div>

          {error ? (
            <p role="alert" className="text-center text-xs text-destructive">
              {error}
            </p>
          ) : null}

          {sent ? (
            <p role="status" className="text-center text-xs text-accent">
              Enviamos o link de acesso para <strong>{sent}</strong>. Verifique sua caixa de entrada.
            </p>
          ) : null}

          <div className="flex items-center justify-center gap-2">
            <Button
              type="submit"
              disabled={loading || checking}
              className="bg-ember shadow-ember h-12 flex-1 text-sm font-semibold tracking-wide text-primary-foreground transition-opacity hover:opacity-90"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Receber acesso por email"}
            </Button>

            {ssoEnabled ? (
              <>
                <span className="text-xs text-muted-foreground">ou</span>
                <Button
                  asChild
                  variant="outline"
                  className="h-12 flex-1 text-sm font-semibold tracking-wide"
                >
                  <a href="/api/public/hotmart/start" onClick={handleHotmartClick}>
                    <LogIn className="mr-2 h-4 w-4" />
                    Entrar com Hotmart
                  </a>
                </Button>
              </>
            ) : null}
          </div>
        </form>
      </section>
    </main>
  );
}
